import React from 'react';
import PropTypes from 'prop-types';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const isSameDay = (a, b) =>
  a && b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

// Renders a calendar grid. Two modes:
//   - Month view (default): `weekStart` is null. Renders a full month for
//     `year` + 0-indexed `month`, leading/trailing blanks padded to a multiple of 7.
//   - Week view: `weekStart` is a Date for the Sunday of the week to render.
//     Always 7 cells, no blanks. Days may span across months at boundaries.
//
// `renderDayContent(date)` and `onDayClick(date)` receive the full Date for
// the cell so callers can derive dots from arbitrary days.
const MonthCalendar = ({ year, month, weekStart, today, darkMode, renderDayContent, onDayClick }) => {
  let cells = []; // (Date | null)[]

  if (weekStart) {
    for (let i = 0; i < 7; i += 1) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      cells.push(d);
    }
  } else {
    const firstOfMonth = new Date(year, month, 1);
    const leadingBlanks = firstOfMonth.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let i = 0; i < leadingBlanks; i += 1) cells.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
  }

  const labelCls = darkMode
    ? 'tw-text-center tw-text-xs tw-font-semibold tw-text-white/60 tw-py-1'
    : 'tw-text-center tw-text-xs tw-font-semibold tw-text-gray-500 tw-py-1';

  const emptyCellCls = darkMode
    ? 'tw-border-transparent tw-bg-transparent'
    : 'tw-border-transparent tw-bg-transparent';

  const dayCellCls = darkMode
    ? 'tw-border-white/10 tw-bg-white/5'
    : 'tw-border-gray-200 tw-bg-white';

  const todayCellCls = darkMode
    ? 'tw-border-blue-400/80 tw-bg-blue-400/10 tw-ring-1 tw-ring-blue-400/50'
    : 'tw-border-blue-500 tw-bg-blue-50 tw-ring-1 tw-ring-blue-300';

  const dayNumberCls = darkMode
    ? 'tw-text-xs sm:tw-text-sm tw-text-white/80 tw-leading-none tw-mb-1'
    : 'tw-text-xs sm:tw-text-sm tw-text-gray-700 tw-leading-none tw-mb-1';

  const todayNumberCls = darkMode
    ? 'tw-text-xs sm:tw-text-sm tw-text-blue-300 tw-font-bold tw-leading-none tw-mb-1'
    : 'tw-text-xs sm:tw-text-sm tw-text-blue-700 tw-font-bold tw-leading-none tw-mb-1';

  return (
    <div className="tw-w-full">
      <div className="tw-grid tw-grid-cols-7 tw-gap-1 tw-mb-1">
        {DAY_LABELS.map((label) => (
          <div key={label} className={labelCls}>
            {label}
          </div>
        ))}
      </div>
      <div className="tw-grid tw-grid-cols-7 tw-gap-1">
        {cells.map((date, idx) => {
          const isEmpty = date === null;
          const clickable = !isEmpty && Boolean(onDayClick);
          const isToday = !isEmpty && isSameDay(date, today);
          return (
            <button
              key={`cell-${idx}`}
              type="button"
              disabled={isEmpty || !onDayClick}
              onClick={clickable ? () => onDayClick(date) : undefined}
              className={[
                'tw-aspect-square tw-rounded-md tw-border tw-flex tw-flex-col tw-items-center tw-justify-start tw-p-1 tw-overflow-hidden',
                isEmpty ? emptyCellCls : (isToday ? todayCellCls : dayCellCls),
                clickable
                  ? (darkMode ? 'hover:tw-bg-white/10 tw-cursor-pointer' : 'hover:tw-bg-gray-50 tw-cursor-pointer')
                  : 'tw-cursor-default',
                'tw-appearance-none',
              ].join(' ')}
            >
              {!isEmpty && (
                <>
                  <span className={isToday ? todayNumberCls : dayNumberCls}>
                    {date.getDate()}
                  </span>
                  {renderDayContent && renderDayContent(date)}
                </>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

MonthCalendar.propTypes = {
  year: PropTypes.number.isRequired,
  month: PropTypes.number.isRequired,
  weekStart: PropTypes.instanceOf(Date),
  today: PropTypes.instanceOf(Date),
  darkMode: PropTypes.bool,
  renderDayContent: PropTypes.func,
  onDayClick: PropTypes.func,
};

export default MonthCalendar;
